import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import Navigation from "@/components/Navigation";
import Breadcrumb from "@/components/Breadcrumb";
import SchemaMarkup from "@/components/SchemaMarkup";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import tarotCardBack from "@/assets/tarot-card-back.jpg";
import eligeTresCartasAudio from "@/assets/elige-tres-cartas.mp3";
import tarotMago from "@/assets/tarot-mago.png";
import tarotSacerdotisa from "@/assets/tarot-sacerdotisa.png";
import tarotEmperatriz from "@/assets/tarot-emperatriz.png";
import tarotEmperador from "@/assets/tarot-emperador.png";
import tarotSumoSacerdote from "@/assets/tarot-sumo-sacerdote.png";
import tarotEnamorados from "@/assets/tarot-enamorados.png";
import tarotCarro from "@/assets/tarot-carro.png";
import tarotFuerza from "@/assets/tarot-fuerza.png";
import tarotErmitano from "@/assets/tarot-ermitano.png";
import tarotRuedaFortuna from "@/assets/tarot-rueda-fortuna.png";
import { createWebApplicationSchema } from "@/utils/schemaHelpers";

const Tarot = () => {
  const [selectedCards, setSelectedCards] = useState<number[]>([]);
  const [revealed, setRevealed] = useState(false);
  const [revealedCards, setRevealedCards] = useState<typeof tarotCards>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const appSchema = createWebApplicationSchema(
    "Lectura de Tarot Gratis - 3 Cartas",
    "Tirada de tarot gratis de 3 cartas para descubrir tu pasado, presente y futuro en amor y destino.",
    "EntertainmentApplication"
  );

  useEffect(() => {
    if (!revealed && !audioRef.current) {
      audioRef.current = new Audio(eligeTresCartasAudio);
      audioRef.current.play().catch(error => {
        if (import.meta.env.DEV) {
          console.log("Audio autoplay prevented:", error);
        }
      });
    }
  }, [revealed]);

  const selectCard = (index: number) => {
    if (selectedCards.length < 3 && !selectedCards.includes(index)) {
      setSelectedCards([...selectedCards, index]);
    }
  };

  const revealReading = () => {
    if (selectedCards.length === 3) {
      // Seleccionar 3 cartas aleatorias del mazo
      const shuffled = [...tarotCards].sort(() => Math.random() - 0.5);
      const selected = shuffled.slice(0, 3);
      
      // Asignar posiciones específicas
      selected[0].position = "Pasado";
      selected[1].position = "Presente";
      selected[2].position = "Futuro";
      
      setRevealedCards(selected);
      setRevealed(true);
    }
  };

  const resetReading = () => {
    setSelectedCards([]);
    setRevealed(false);
    setRevealedCards([]);
  };

  return (
    <>
      <Helmet>
        <title>Lectura de Tarot Gratis - Tirada de 3 Cartas | Dstino</title>
        <meta name="description" content="Lectura de tarot gratis online. Tirada de 3 cartas para descubrir tu pasado, presente y futuro en amor y destino. Consulta el tarot ahora." />
        <meta property="og:title" content="Lectura de Tarot Gratis - 3 Cartas" />
        <meta property="og:description" content="Tirada de tarot gratis para amor y destino. Descubre tu pasado, presente y futuro." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`${window.location.origin}/tarot`} />
      </Helmet>

      <SchemaMarkup schema={appSchema} />

      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="pt-24 pb-12 px-4">
          <div className="container mx-auto max-w-6xl">
            <Breadcrumb 
              items={[
                { name: "Inicio", url: "/" },
                { name: "Tarot" }
              ]}
            />

            <h1 className="text-5xl font-bold text-center mb-4 text-glow">
              Lectura de Tarot Gratis
            </h1>
            <p className="text-center text-xl text-muted-foreground mb-12">
              La Tirada de las 3 Cartas para el Amor y el Destino
            </p>

            {!revealed ? (
            <>
              <Card className="p-8 gradient-celestial mb-8">
                <h2 className="text-2xl font-bold mb-4 text-center">
                  Elige 3 Cartas
                </h2>
                <p className="text-center text-muted-foreground mb-8">
                  Concéntrate en tu pregunta y selecciona 3 cartas que llamen tu atención
                </p>

                <div className="grid grid-cols-3 md:grid-cols-5 gap-4 mb-8">
                  {Array.from({ length: 10 }).map((_, index) => (
                    <div
                      key={index}
                      onClick={() => selectCard(index)}
                      className={`aspect-[2/3] rounded-lg cursor-pointer transition-all duration-300 overflow-hidden ${
                        selectedCards.includes(index)
                          ? 'card-glow scale-105 bg-primary/30 border-2 border-primary'
                          : 'bg-card hover:bg-card/80 border border-border'
                      }`}
                    >
                      <div className="w-full h-full flex items-center justify-center">
                        {selectedCards.includes(index) ? (
                          <Sparkles className="w-12 h-12 text-primary" />
                        ) : (
                          <img 
                            src={tarotCardBack} 
                            alt="Dstino Tarot Card" 
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="text-center">
                  <p className="mb-4 text-lg">
                    Cartas seleccionadas: {selectedCards.length}/3
                  </p>
                  <Button
                    onClick={revealReading}
                    disabled={selectedCards.length !== 3}
                    size="lg"
                  >
                    Revelar Mi Lectura
                  </Button>
                </div>
              </Card>
              </>
            ) : (
            <div className="space-y-8">
              <Card className="p-8 gradient-mystical">
                <h2 className="text-3xl font-bold mb-8 text-center">
                  Tu Lectura del Tarot
                </h2>

                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  {revealedCards.map((card, idx) => (
                    <div key={idx} className="card-flip-container" style={{ animationDelay: `${idx * 200}ms` }}>
                      <Card className="p-6 bg-card/50 backdrop-blur card-flip" style={{ animationDelay: `${idx * 200}ms` }}>
                        <div className="aspect-[2/3] rounded-lg overflow-hidden mb-4 shadow-lg">
                          <img 
                            src={card.image} 
                            alt={card.name} 
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <h3 className="text-xl font-bold mb-2 text-center text-primary">
                          {card.name}
                        </h3>
                        <p className="text-sm text-center text-muted-foreground mb-4">
                          {card.position}
                        </p>
                        <p className="text-muted-foreground leading-relaxed">
                          {card.meaning}
                        </p>
                      </Card>
                    </div>
                  ))}
                </div>

                <div className="bg-card/50 backdrop-blur rounded-lg p-6">
                  <h3 className="text-2xl font-bold mb-4 text-center text-primary">
                    Interpretación General
                  </h3>
                  <p className="text-muted-foreground leading-relaxed text-center">
                    Las cartas revelan un momento de transformación en tu vida. 
                    El pasado te ha preparado para los desafíos actuales, y el futuro 
                    promete nuevas oportunidades si mantienes una actitud positiva y 
                    abierta al cambio. Confía en tu intuición y sigue adelante con 
                    valentía.
                  </p>
                </div>

                <div className="text-center mt-8">
                  <Button onClick={resetReading} variant="outline" size="lg">
                    Nueva Lectura
                  </Button>
                </div>
              </Card>
            </div>
            )}

            {/* Information Section */}
          <Card className="p-8 gradient-celestial mt-8">
            <h2 className="text-2xl font-bold mb-4">
              Sobre la Tirada de 3 Cartas
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                La tirada de tres cartas es una de las lecturas más populares del tarot. 
                Cada carta representa un aspecto diferente de tu situación:
              </p>
              <ul className="space-y-2 list-disc list-inside">
                <li><strong className="text-foreground">Pasado:</strong> Los acontecimientos y energías que te han llevado al momento presente</li>
                <li><strong className="text-foreground">Presente:</strong> Tu situación actual y los desafíos o oportunidades que enfrentas ahora</li>
                <li><strong className="text-foreground">Futuro:</strong> El resultado probable si continúas en tu camino actual</li>
              </ul>
              <p>
                Recuerda que el tarot es una herramienta de autoconocimiento. Las cartas 
                te ayudan a reflexionar sobre tu vida y tus decisiones, pero tú siempre 
                tienes el poder de crear tu propio destino.
              </p>
            </div>
            </Card>
          </div>
        </main>
      </div>
    </>
  );
};

const tarotCards = [
  {
    name: "El Mago",
    image: tarotMago,
    position: "Pasado",
    meaning: "Representa el poder de manifestar los sueños en la realidad. Es la conexión entre el mundo espiritual y el material. Invita a usar creatividad, habilidades y recursos para transformar tu realidad."
  },
  {
    name: "La Sacerdotisa",
    image: tarotSacerdotisa,
    position: "Presente",
    meaning: "Simboliza la intuición, el misterio y el conocimiento oculto. Es la guardiana de los secretos del alma. Aconseja escuchar la voz interior antes de actuar. Confía en tu sabiduría interna."
  },
  {
    name: "La Emperatriz",
    image: tarotEmperatriz,
    position: "Futuro",
    meaning: "Encarna la abundancia, la fertilidad y el amor maternal. Representa la belleza, la creatividad y la conexión con la naturaleza. Un augurio de crecimiento y prosperidad en todos los aspectos de tu vida."
  },
  {
    name: "El Emperador",
    image: tarotEmperador,
    position: "Pasado",
    meaning: "Simboliza la autoridad, la estructura y el liderazgo. Representa la estabilidad y la razón. Señala control, poder y orden interior."
  },
  {
    name: "El Sumo Sacerdote",
    image: tarotSumoSacerdote,
    position: "Presente",
    meaning: "Encierra la sabiduría ancestral y la guía espiritual. Representa las tradiciones y la enseñanza. Inspira a buscar consejo y a seguir la fe."
  },
  {
    name: "Los Enamorados",
    image: tarotEnamorados,
    position: "Futuro",
    meaning: "Carta del amor, la unión y las decisiones del corazón. Representa la dualidad y la elección consciente. Invita a la armonía y al compromiso."
  },
  {
    name: "El Carro",
    image: tarotCarro,
    position: "Pasado",
    meaning: "Simboliza el triunfo por medio de la determinación. Representa el avance, la dirección y el dominio de las emociones. Señala éxito tras el esfuerzo."
  },
  {
    name: "La Fuerza",
    image: tarotFuerza,
    position: "Presente",
    meaning: "Es el poder de la calma, la compasión y el valor interior. Dominar sin violencia, guiar con ternura. Indica autocontrol y fortaleza emocional."
  },
  {
    name: "El Ermitaño",
    image: tarotErmitano,
    position: "Futuro",
    meaning: "Refleja la búsqueda interior y la sabiduría solitaria. Representa el aprendizaje a través de la introspección. Invita al silencio y la reflexión."
  },
  {
    name: "La Rueda de la Fortuna",
    image: tarotRuedaFortuna,
    position: "Presente",
    meaning: "Simboliza el cambio, el destino y los ciclos de la vida. Representa oportunidades que giran a tu favor. Aconseja aceptar el movimiento constante del universo."
  }
];

export default Tarot;
